import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Snackbar,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Add,
  Delete,
} from "@mui/icons-material";
import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

const AllUsers = () => {
  const selectedManufacturer = JSON.parse(localStorage.getItem("selectedMfr"));
  const mfr_id = selectedManufacturer?.id;
  const adminUser = JSON.parse(localStorage.getItem("user"));

  // console.log(selectedManufacturer);
  // console.log(mfr_id);
  // console.log(adminUser);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [openRows, setOpenRows] = useState({});
  const [grantDialog, setGrantDialog] = useState({ open: false, userId: null });
  const [selectedLinkId, setSelectedLinkId] = useState("");
  const [allLinks, setAllLinks] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  // Fetch users + their accessible links
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/auth/users-with-link-access/${mfr_id}`,
      );
      if (res.data.success) {
        setUsers(res.data.data);
      }
      // Also fetch all links for the grant dropdown
      const linksRes = await axios.get(
        `${API_BASE_URL}/api/auth/links/${mfr_id}`,
      );
      if (linksRes.data.statusCode === 200) {
        const sortedLinks = [...linksRes.data.data].sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {
            sensitivity: "base",
          }),
        );

        setAllLinks(sortedLinks);
      }
    } catch (error) {
      console.error(error);
      setSnackbar({ open: true, message: "Failed to load users" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mfr_id) fetchData();
  }, [mfr_id]);

  const handleRevoke = async (userId, linkId) => {
    try {
      await axios.post(`${API_BASE_URL}/api/auth/revoke-access`, {
        link_id: linkId,
        user_id: userId,
        admin_id: adminUser?.id,
      });
      setSnackbar({ open: true, message: "Access revoked successfully" });
      fetchData(); // refresh
    } catch (error) {
      setSnackbar({ open: true, message: "Revoke failed" });
    }
  };

  const handleGrant = async () => {
    if (!selectedLinkId || !grantDialog.userId) return;
    try {
      await axios.post(`${API_BASE_URL}/api/auth/grant-direct-access`, {
        link_id: selectedLinkId,
        user_id: grantDialog.userId,
        admin_id: adminUser?.id,
      });
      setSnackbar({ open: true, message: "Access granted successfully" });
      setGrantDialog({ open: false, userId: null });
      setSelectedLinkId("");
      fetchData();
    } catch (error) {
      setSnackbar({ open: true, message: "Grant failed" });
    }
  };

  const toggleRow = (userId) => {
    setOpenRows((prev) => ({ ...prev, [userId]: !prev[userId] }));
  };

  const filteredUsers = users.filter(
    (user) =>
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.mobile?.includes(searchTerm),
  );

  return (
    <Box sx={{ bgcolor: "#394A5C", minHeight: "100vh", p: { xs: 2, md: 4 } }}>
      <Typography variant="h4" sx={{ color: "#fff", mb: 2, fontWeight: 600 }}>
        User Access Management
      </Typography>

      <TextField
        placeholder="Search by name or mobile"
        variant="outlined"
        size="small"
        fullWidth
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ bgcolor: "#fff", borderRadius: 1, mb: 3, maxWidth: 400 }}
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress sx={{ color: "#42A996" }} />
        </Box>
      ) : (
        <Table sx={{ bgcolor: "#1E2F40", borderRadius: 2, overflow: "hidden" }}>
          <TableHead>
            <TableRow sx={{ bgcolor: "#2A3A4B" }}>
              <TableCell width={50} />
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Name
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Mobile
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Accessible Links
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <React.Fragment key={user.user_id}>
                <TableRow hover sx={{ "&:hover": { bgcolor: "#2A3A4B" } }}>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => toggleRow(user.user_id)}
                    >
                      {openRows[user.user_id] ? (
                        <KeyboardArrowUp sx={{ color: "#fff" }} />
                      ) : (
                        <KeyboardArrowDown sx={{ color: "#fff" }} />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ color: "#fff" }}>{user.name}</TableCell>
                  <TableCell sx={{ color: "#fff" }}>{user.mobile}</TableCell>
                  <TableCell sx={{ color: "#fff" }}>
                    {user.accessibleLinks.length} link(s)
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Add />}
                      onClick={() =>
                        setGrantDialog({ open: true, userId: user.user_id })
                      }
                      sx={{
                        bgcolor: "#42A996",
                        "&:hover": { bgcolor: "#3b927f" },
                      }}
                    >
                      Grant Access
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    colSpan={5}
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                  >
                    <Collapse
                      in={openRows[user.user_id]}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box sx={{ margin: 2 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: "#BFCBD7", mb: 1 }}
                        >
                          Links this user can access:
                        </Typography>
                        {user.accessibleLinks.length === 0 ? (
                          <Typography
                            sx={{ color: "#BFCBD7", fontStyle: "italic" }}
                          >
                            No links accessible
                          </Typography>
                        ) : (
                          <Box component="ul" sx={{ m: 0, pl: 2 }}>
                            {user.accessibleLinks.map((link) => (
                              <li key={link.linkId} style={{ marginBottom: 8 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                  }}
                                >
                                  <Typography sx={{ color: "#fff" }}>
                                    {link.name} ({link.type})
                                  </Typography>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    startIcon={<Delete />}
                                    onClick={() =>
                                      handleRevoke(user.user_id, link.linkId)
                                    }
                                  >
                                    Revoke
                                  </Button>
                                </Box>
                              </li>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Grant Access Dialog */}
      <Dialog
        open={grantDialog.open}
        onClose={() => setGrantDialog({ open: false, userId: null })}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            width: 600,
            maxWidth: "95vw",
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            fontSize: "1.4rem",
            fontWeight: 600,
          }}
        >
          Grant Access to Link
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
            Select Link
          </Typography>

          <Autocomplete
            options={[...allLinks].sort((a, b) =>
              a.name.localeCompare(b.name, undefined, {
                sensitivity: "base",
              }),
            )}
            value={
              allLinks.find((link) => link.linkId === selectedLinkId) || null
            }
            onChange={(event, newValue) => {
              setSelectedLinkId(newValue?.linkId || "");
            }}
            getOptionLabel={(option) => `${option.name} (${option.type})`}
            renderInput={(params) => (
              <TextField {...params} placeholder="Search link..." fullWidth />
            )}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setGrantDialog({ open: false, userId: null })}>
            Cancel
          </Button>

          <Button
            onClick={handleGrant}
            variant="contained"
            sx={{ bgcolor: "#42A996" }}
          >
            Grant
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        message={snackbar.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
};

export default AllUsers;
